/*
 * Copyright (C) 2025 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { ApiRootLinkRel, ApiRootResource, ApiRootService } from '@common/api-root-shared';
import {
  isNotUndefined,
  mapToResource,
  ResourceRepository,
  ResourceSearchService,
  SearchResourceServiceConfig,
} from '@common/tech-shared';
import {
  OrganisationsEinheit,
  OrganisationsEinheitListResource,
  OrganisationsEinheitResource,
  OrganisationsEinheitService,
  ZUSTAENDIGE_STELLE_SERVICE,
} from '@alfa-client/zustaendige-stelle-shared';
import { Component, EventEmitter, inject, Input, Output, ViewContainerRef } from '@angular/core';
import { OzgcloudDialogService } from '@ods/component';
import { ButtonComponent, IconComponent } from '@ods/system';
import { filter, first } from 'rxjs';
import { SearchZustaendigeStelleDialogComponent } from '../search-zustaendige-stelle-dialog/search-zustaendige-stelle-dialog.component';

@Component({
  selector: 'alfa-organisation-einheit-in-online-dienst',
  imports: [ButtonComponent, IconComponent],
  providers: [
    {
      provide: ZUSTAENDIGE_STELLE_SERVICE,
      useFactory: createOrganisationEinheitService,
      deps: [ResourceRepository, ApiRootService],
    },
  ],
  templateUrl: './organisation-einheit-in-online-dienst.component.html',
})
export class OrganisationEinheitInOnlineDienstComponent {
  private readonly dialogService = inject(OzgcloudDialogService);
  private readonly viewContainerRef = inject(ViewContainerRef);

  @Input() organisationEinheitResource: OrganisationsEinheit;

  @Output() public organisationsEinheitResourceEmitter: EventEmitter<OrganisationsEinheitResource> =
    new EventEmitter<OrganisationsEinheitResource>();

  openSearchDialog(): void {
    this.dialogService
      .openFullScreenDialog<SearchZustaendigeStelleDialogComponent<OrganisationsEinheitResource>>(
        SearchZustaendigeStelleDialogComponent,
        this.viewContainerRef,
        'Organisationseinheit zuweisen',
      )
      .closed.pipe(first(), filter(isNotUndefined))
      .subscribe((organisationsEinheitResource: OrganisationsEinheitResource) => {
        this.organisationEinheitResource = organisationsEinheitResource;
        this.organisationsEinheitResourceEmitter.emit(organisationsEinheitResource);
      });
  }
}

function createOrganisationEinheitService(
  resourceRepository: ResourceRepository,
  apiRootService: ApiRootService,
): OrganisationsEinheitService {
  return new OrganisationsEinheitService(createSearchService(resourceRepository, apiRootService));
}

function createSearchService(
  resourceRepository: ResourceRepository,
  apiRootService: ApiRootService,
): ResourceSearchService<ApiRootResource, OrganisationsEinheitListResource, OrganisationsEinheitResource> {
  return new ResourceSearchService<ApiRootResource, OrganisationsEinheitListResource, OrganisationsEinheitResource>(
    buildConfig(apiRootService),
    resourceRepository,
  );
}

function buildConfig(apiRootService: ApiRootService): SearchResourceServiceConfig<ApiRootResource> {
  return {
    baseResource: apiRootService.getApiRoot().pipe(mapToResource<ApiRootResource>()),
    searchLinkRel: ApiRootLinkRel.SEARCH_ORGANISATIONS_EINHEIT,
  };
}
